import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useDeleteOversightAssessments } from 'src/hooks/mutations/oversight-assessment';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useGetOversightAssessmentById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';

import useExporter from './useExporter';

type Props = {
  activeTabId:
    | 'activities'
    | 'details'
    | 'findings'
    | 'impacts'
    | 'linkedItems';
  activityMode?: 'addActivity' | 'list' | 'updateActivity' | undefined;
};

const Page: FC<Props> = ({ activeTabId, activityMode = undefined }) => {
  const { t } = useTranslation(['common']);
  const assessmentId = useGetGuidParam('assessmentId');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const [exportItem, { loading: exporting }] = useExporter(assessmentId);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const navigate = useNavigate();
  const parentUrl = useGetDetailParentPath(assessmentId);
  const detailsPath = useGetDetailPath(assessmentId);
  const { deleteOversightAssessments, loading: deleteLoading } =
    useDeleteOversightAssessments();

  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'complianceMonitoringAssessment',
  });
  const { data, loading, error } = useGetOversightAssessmentById({
    queryArgs: { id: assessmentId },
  });
  if (error) {
    throw error;
  }

  const assessment = data?.oversight_assessment[0];
  const tabs = useTabs({
    parentType: Parent_Type_Enum.OversightAssessment,
    parent: assessment,
    assessmentActivityMode: activityMode,
    assessmentMode: 'oversight_assessment',
    hrefRoot: detailsPath,
  });
  const onDelete = useDeleteResultNotification({
    entityName: st('entity'),
    asyncAction: async () => {
      if (!assessment) {
        return false;
      }
      await deleteOversightAssessments([assessment.Id]);
      await navigate(parentUrl);

      return true;
    },
  });

  if (!loading && !assessment?.Id) {
    throw new PageNotFound(`Assessment with id ${assessmentId} not found`);
  }

  const counter =
    assessment &&
    `(${getFriendlyId(
      Parent_Type_Enum.OversightAssessment,
      assessment.SequentialId
    )})`;
  const fallbackTitle = st('fallback_title');

  // Object-level toolbar (CUS-164) — flag-gated. Relocates Export + Delete into
  // the toolbar's overflow ⋮ menu and surfaces the Save disc / Cancel X (save
  // state bridged from the details tab's form via useObjectToolbarFormStore).
  // Parity note: the legacy page nests BOTH Export and Delete inside a single
  // `delete:oversight_assessment` Permission, so the toolbar gates the Export
  // action on the same permission (not ungated like the Actions reference) to
  // keep flag-on/off visibility identical.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: assessment?.Title || fallbackTitle,
        counter: counter || undefined,
      },
      parentObject: assessment,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('objectToolbar.formSettings'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('objectToolbar.tabSettings'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
                permission: 'delete:oversight_assessment',
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            label: st('delete_button'),
            iconName: 'trash-01',
            permission: 'delete:oversight_assessment',
            // Hydration gate (ENG-616) — the legacy button already has it.
            disabled: !assessment,
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [
      assessment,
      fallbackTitle,
      counter,
      t,
      st,
      exportItem,
      exporting,
      formConfig,
    ]
  );

  return (
    <PageLayout
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <Permission
          permission={'delete:oversight_assessment'}
          parentObject={assessment}
        >
          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Button
              iconName={'download'}
              disabled={exporting}
              onClick={exportItem}
            >
              {t('export.export')}
            </Button>
            <Button
              variant={'normal'}
              formAction={'none'}
              // Wait for the entity to hydrate. `onDelete` guards on
              // `if (!assessment) return false`, which surfaces as a bare
              // "Failed to save changes" with NO request issued, so a click
              // inside the hydration window looks like a broken delete
              // (ENG-616). Disabling also restores Playwright's actionability
              // wait.
              disabled={!assessment}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
            >
              {st('delete_button')}
            </Button>
          </SpaceBetween>
        </Permission>
      }
      meta={{
        title: fallbackTitle,
      }}
      title={assessment?.Title}
      counter={counter}
    >
      <ControlledTabs
        tabs={tabs}
        activeTabId={activeTabId}
        variant={'container'}
        parentType={Parent_Type_Enum.OversightAssessment}
        parent={assessment}
        disableSettings={objectToolbarEnabled}
      />

      <TabSettingsModal
        parentType={Parent_Type_Enum.ComplianceMonitoringAssessment}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={assessment}
      />

      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
